import React, { useCallback, useMemo, useRef } from 'react';

import { type InternalRefetchQueriesInclude } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { CircularProgress, Grid } from '@mui/material';
import { type DeepPartial } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  type GetPortalQuery,
  Portals_Types_Enum_Enum,
} from '@graphql/__generated__/graphql';
import {
  type NonInternalProps,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import { useImageRegistration } from '@modules/_shared/components/image-upload/useImageUpload';
import { MutationErrorAlert } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { PortalConnectionStatus } from '@modules/portals/components/PortalConnectionStatus';
import {
  type PortalSettingsFormData,
  formDefinition,
} from '@modules/portals/portalFormDefinition';
import {
  CHECK_PORTAL_CONNECTION,
  GET_PORTAL,
  GET_PORTAL_METADATA,
  UPSERT_PORTAL,
} from '@modules/portals/portalsQueries';
import { useLocale } from '@shared/hooks/locale';

type PortalSettingsProps = {
  onClose: () => void;
  refetchQueries?: InternalRefetchQueriesInclude;
  onUpsert?: (portalId: string) => void;
};

export const PortalSettings: React.FC<PortalSettingsProps> = ({
  onClose,
  refetchQueries,
  onUpsert,
}) => {
  const { t } = useLocale();
  const { portalId, teamId } = useParams();
  const { me } = useAppData();
  const apolloClient = useApolloClient();
  const topRef = useRef<HTMLDivElement>(null);

  const {
    data: portalTypesData,
    loading: portalTypesLoading,
    error: queryPortalMetadataError,
  } = useQuery(GET_PORTAL_METADATA);

  const {
    loading,
    data,
    error: queryPortalError,
  } = useQuery(GET_PORTAL, {
    variables: { id: portalId ?? '' },
    skip: portalId == null,
  });

  const isEditing = !!data?.portals_by_pk?.id;

  const shared_portals = portalTypesData?.shared_portals ?? [];
  const portals_types_enum = portalTypesData?.portals_types_enum ?? [];

  const [upsertPortal] = useMutation(UPSERT_PORTAL, {
    onCompleted: data => {
      if (data?.insert_portals_one != null) {
        onUpsert?.(data.insert_portals_one.id);
      }
    },
    update: (cache, { data }) => {
      if (data?.insert_portals_one != null) {
        cache.writeQuery<GetPortalQuery>({
          query: GET_PORTAL,
          variables: { id: portalId },
          data: {
            portals_by_pk: data.insert_portals_one,
          },
        });
      }
    },
    refetchQueries,
  });

  const initialLoadingError = queryPortalError ?? queryPortalMetadataError;

  const { registerImage } = useImageRegistration();

  const connectionFieldsHaveChanged = useCallback(
    (formData: NonInternalProps<PortalSettingsFormData>) => {
      return (
        formData.url !== data?.portals_by_pk?.url ||
        formData.port !== data?.portals_by_pk?.port ||
        formData.username !== data?.portals_by_pk?.username ||
        formData.password !== data?.portals_by_pk?.password ||
        formData.idx_protocol !== data?.portals_by_pk?.idx_protocol
      );
    },
    [data?.portals_by_pk],
  );

  const onSubmit = useCallback(
    async (formData: NonInternalProps<PortalSettingsFormData>) => {
      const result = await upsertPortal({
        variables: {
          portal: {
            ...formData,
            id: portalId,
          },
        },
      });

      if (
        isEditing &&
        data?.portals_by_pk?.type === Portals_Types_Enum_Enum.Idx &&
        connectionFieldsHaveChanged(formData)
      ) {
        let el = topRef.current?.parentElement;
        while (el != null) {
          if (el.scrollHeight > el.clientHeight) {
            el.scrollTo({ top: 0, behavior: 'smooth' });
            break;
          }
          el = el.parentElement;
        }
        apolloClient.refetchQueries({ include: [CHECK_PORTAL_CONNECTION] });
      }

      return result;
    },
    [
      isEditing,
      apolloClient,
      upsertPortal,
      portalId,
      connectionFieldsHaveChanged,
      data?.portals_by_pk?.type,
    ],
  );

  const defaultValues = useMemo<DeepPartial<PortalSettingsFormData>>(
    () => ({
      created_at: data?.portals_by_pk?.created_at,
      created_by: data?.portals_by_pk?.created_by,
      active: data?.portals_by_pk?.active,
      contract_id: data?.portals_by_pk?.contract_id,
      email: data?.portals_by_pk?.email,
      exact_address_required: data?.portals_by_pk?.exact_address_required,
      exclude_from_published_stats:
        data?.portals_by_pk?.exclude_from_published_stats,
      has_top_premium_listing: data?.portals_by_pk?.has_top_premium_listing,
      idx_protocol: data?.portals_by_pk?.idx_protocol,
      master_portal_id: data?.portals_by_pk?.master_portal_id,
      name: data?.portals_by_pk?.name,
      password: data?.portals_by_pk?.password,
      port: data?.portals_by_pk?.port,
      quota: data?.portals_by_pk?.quota,
      type: data?.portals_by_pk?.type,
      url: data?.portals_by_pk?.url,
      username: data?.portals_by_pk?.username,
      website_url: data?.portals_by_pk?.website_url,
      team_id: data?.portals_by_pk?.team_id ?? teamId,
      logo_image_id: data?.portals_by_pk?.logo?.id ?? null,
    }),
    [data?.portals_by_pk, teamId],
  );

  if (initialLoadingError != null) {
    return <MutationErrorAlert error={initialLoadingError} />;
  }

  return (
    <>
      {loading || portalTypesLoading ? (
        <Grid
          container
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress disableShrink />
        </Grid>
      ) : (
        <>
          <PortalConnectionStatus ref={topRef} portal={data?.portals_by_pk} />
          <RaForm
            formDefinition={formDefinition}
            context={{
              shared_portals,
              portals_types_enum,
              disableTeamId: !!teamId || !me?.is_admin,
              isCreation: data?.portals_by_pk?.id == null,
              registerImage,
              readOnly: !me?.is_admin,
            }}
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            submitButtonText={isEditing ? t('Update portal') : t('Add portal')}
            onCancel={isDirty => {
              if (!isDirty) {
                onClose();
              }
            }}
          />
        </>
      )}
    </>
  );
};
